import { useAtom } from 'jotai';
import moment from 'moment';
import React, { FC, useState } from 'react';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ReservationLiveClassDateAndTime } from '../components/ReservationLiveClassDateAndTime';
import { GoogleAnalyticsEvents } from '../enum/GoogleAnalyticsEvents';
import { reservationDataAtom } from '../jotai-atoms/reservation-data.atom';
import { LiveClassBookingService } from '../services/liveclass-booking';
import { LiveClassShiftListItemProps } from './LiveClassShiftListItem';
import { LiveClassTag, LiveClassType } from './LiveClassTag';
import { Modal, ModalProps } from './Modal';

export interface ModalReservationReserveProps {
  exampleBg?: boolean;
  show: boolean;
  onClose: () => void;
  liveClass?: LiveClassShiftListItemProps;
}

export const ModalReservationReserve: FC<ModalReservationReserveProps> = ({
  exampleBg,
  show,
  onClose,
  liveClass,
}) => {
  const __ = useTranslation().t;

  const liveClassType = liveClass?.type;
  const [reservationData, setReservationData] = useAtom(reservationDataAtom);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const modalProps: ModalProps = {
    show,
    onClose,
    type: 'default',
    exampleBg,
    title: __('confirmClass'),
    submitButtonText: __('confirm'),
    cancelButtonText: __('cancel'),
  };

  const logEvent = (type: LiveClassType) => {
    switch (type) {
      case 'gameclass':
        ReactGA.event(GoogleAnalyticsEvents.BookGameClass);
        break;
      case 'group':
        ReactGA.event(GoogleAnalyticsEvents.BookGroupClass);
        break;
      case 'orientation':
        ReactGA.event(GoogleAnalyticsEvents.BookOrientationClass);
        break;
      case 'private':
        ReactGA.event(GoogleAnalyticsEvents.BookPrivateClass);
        break;
    }
  };

  const onSubmit = () => {
    if (!liveClass || !liveClass.dateTime || !liveClass.topicId || !liveClass.type) {
      return;
    }

    setIsLoading(true);

    logEvent(liveClass.type);

    LiveClassBookingService.postReserve({
      datetime: moment.utc(liveClass?.dateTime).format('YYYY-MM-DD HH:mm:ss'),
      topic_id: liveClass.topicId,
      type_id: liveClass.type,
    })
      .then((response) => {
        if (response.data?.status === 'error') {
          throw new Error();
        }
        setIsLoading(false);
        setReservationData({
          ...reservationData,
          activeModal: null,
          forceCheckNotification: !reservationData?.forceCheckNotification,
        });
      })
      .catch(() => {
        setIsLoading(false);
        alert(__('whopsSomethingWentWrong'));
      });
  };

  return (
    <StyledModal
      {...modalProps}
      onSubmit={onSubmit}
      submitButtonDisabled={isLoading}
      cancelButtonDisabled={isLoading}
    >
      <LiveClassTag type={liveClassType} />
      <StyledH4>{liveClass?.title}</StyledH4>
      <ReservationLiveClassDateAndTime dateTime={liveClass?.dateTime} />
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  max-width: 530px;
`;

const StyledH4 = styled.h4`
  font-family: 'Baloo 2';
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.dark};
  margin: 8px 0 !important;
`;
